import { Fragment, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import StatusBadge, { emailStatusTone } from "@/components/admin/StatusBadge";
import { Loader2, Mail, ShieldAlert, ChevronLeft, ChevronRight } from "lucide-react";

interface EmailRow {
  id: string;
  message_id: string | null;
  template_name: string | null;
  recipient_email: string | null;
  status: string;
  error_message: string | null;
  created_at: string;
}

const RANGES = {
  "24h": 1,
  "7d": 7,
  "30d": 30,
} as const;
type RangeKey = keyof typeof RANGES;

const PAGE_SIZE = 50;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "short", timeStyle: "short" });

const AdminEmail = () => {
  const [rows, setRows] = useState<EmailRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState<RangeKey>("7d");
  const [template, setTemplate] = useState<string>("all");
  const [status, setStatus] = useState<string>("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      setLoading(true);
      const since = new Date();
      since.setDate(since.getDate() - RANGES[range]);
      const { data, error } = await supabase
        .from("email_send_log")
        .select("*")
        .gte("created_at", since.toISOString())
        .order("created_at", { ascending: false })
        .limit(5000);
      if (!active) return;
      if (!error && data) setRows(data as EmailRow[]);
      setLoading(false);
      setPage(0);
    })();
    return () => {
      active = false;
    };
  }, [range]);

  // Deduplicate by message_id, keeping latest (rows already sorted DESC)
  const deduped = useMemo(() => {
    const seen = new Set<string>();
    const out: EmailRow[] = [];
    for (const r of rows) {
      const key = r.message_id ?? r.id;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(r);
    }
    return out;
  }, [rows]);

  const templates = useMemo(() => {
    const set = new Set<string>();
    deduped.forEach((r) => r.template_name && set.add(r.template_name));
    return Array.from(set).sort();
  }, [deduped]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return deduped.filter((r) => {
      if (template !== "all" && r.template_name !== template) return false;
      if (status !== "all") {
        if (status === "failed" && !["dlq", "failed", "bounced", "complained"].includes(r.status))
          return false;
        if (status !== "failed" && r.status !== status) return false;
      }
      if (q && !(r.recipient_email ?? "").toLowerCase().includes(q)) return false;
      return true;
    });
  }, [deduped, template, status, search]);

  const stats = useMemo(() => {
    const s = { total: deduped.length, sent: 0, failed: 0, suppressed: 0, pending: 0 };
    for (const r of deduped) {
      if (r.status === "sent") s.sent++;
      else if (["dlq", "failed", "bounced", "complained"].includes(r.status)) s.failed++;
      else if (r.status === "suppressed") s.suppressed++;
      else if (r.status === "pending") s.pending++;
    }
    return s;
  }, [deduped]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  return (
    <>
      <Helmet>
        <title>Email · Admin</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight">Email</h1>
          <p className="text-muted-foreground mt-1">
            Delivery status of every email sent from the site.
          </p>
        </div>
        <Button asChild variant="outline">
          <Link to="/admin/emails/suppressions">
            <ShieldAlert className="w-4 h-4 mr-2" /> Suppression list
          </Link>
        </Button>
      </header>

      <section className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        {[
          { label: "Total", value: stats.total },
          { label: "Sent", value: stats.sent },
          { label: "Failed", value: stats.failed },
          { label: "Suppressed", value: stats.suppressed },
        ].map((s) => (
          <div key={s.label} className="rounded-lg border border-border bg-card p-4">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              {s.label}
            </div>
            <div className="text-2xl font-semibold mt-2">{s.value}</div>
          </div>
        ))}
      </section>

      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <Select value={range} onValueChange={(v) => setRange(v as RangeKey)}>
          <SelectTrigger className="w-full sm:w-36">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Last 24h</SelectItem>
            <SelectItem value="7d">Last 7 days</SelectItem>
            <SelectItem value="30d">Last 30 days</SelectItem>
          </SelectContent>
        </Select>
        <Select value={template} onValueChange={setTemplate}>
          <SelectTrigger className="w-full sm:w-56">
            <SelectValue placeholder="Template" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All templates</SelectItem>
            {templates.map((t) => (
              <SelectItem key={t} value={t}>
                {t}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-full sm:w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="sent">Sent</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="suppressed">Suppressed</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
          </SelectContent>
        </Select>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search recipient…"
          className="flex-1"
        />
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <div className="overflow-x-auto">
        {loading ? (
          <div className="p-10 flex items-center justify-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading…
          </div>
        ) : pageRows.length === 0 ? (
          <div className="p-10 flex flex-col items-center justify-center text-muted-foreground text-sm">
            <Mail className="w-8 h-8 mb-2 opacity-60" />
            No emails match your filters.
          </div>
        ) : (
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-2 font-medium">Status</th>
                <th className="text-left px-4 py-2 font-medium">Template</th>
                <th className="text-left px-4 py-2 font-medium">Recipient</th>
                <th className="text-left px-4 py-2 font-medium">When</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r) => {
                const isFailed = ["dlq", "failed", "bounced", "complained"].includes(r.status);
                const isOpen = expanded === r.id;
                return (
                  <Fragment key={r.id}>
                    <tr
                      onClick={() => isFailed && setExpanded(isOpen ? null : r.id)}
                      className={`border-t border-border ${
                        isFailed ? "cursor-pointer hover:bg-muted/30" : ""
                      }`}
                    >
                      <td className="px-4 py-3">
                        <StatusBadge label={r.status} tone={emailStatusTone(r.status)} />
                      </td>
                      <td className="px-4 py-3 font-mono text-xs">{r.template_name ?? "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground">{r.recipient_email ?? "—"}</td>
                      <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                        {fmtDate(r.created_at)}
                      </td>
                    </tr>
                    {isOpen && r.error_message && (
                      <tr className="bg-red-500/5">
                        <td colSpan={4} className="px-4 py-3 text-xs font-mono text-red-700 dark:text-red-300 whitespace-pre-wrap">
                          {r.error_message}
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
        </div>
      </div>

      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-4 text-sm text-muted-foreground">
          <span>
            Page {page + 1} of {totalPages} · {filtered.length} emails
          </span>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={page === 0}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
              disabled={page >= totalPages - 1}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminEmail;
